import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import MKBox from 'components/MaterialKit/MKBox';
import { handleErrorResponse } from 'utils/general';
import { withLoading } from 'utils/hoc';
import { useAuth } from 'contexts/auth';
import { getLocaleMap } from 'utils/locales';
import { getConfiguration, updateConfiguration } from 'api/configurations';
import EditConfigurationForm from './EditConfigurationForm';

const EditConfigurationSection = ({
  setLoading,
  title_color,
  title_font_size,
  title_font_weight,
  edit_button_color,
  edit_font_color,
  save_button_color,
  save_button_font_color,
  back_button_color,
  back_button_font_color,
}) => {
  const [lm, setLm] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getLocaleMap(['labels_edit', 'labels_create', 'configurations'])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const [configuration, setConfiguration] = useState(null);
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuth();
  const configurationId = searchParams.get('configuration_id');

  const fetchConfigurationFromApi = useCallback(() => {
    if (!configurationId) {
      return Promise.resolve();
    }
    setLoading(true);
    return getConfiguration(configurationId)
      .then(({ data }) => {
        setConfiguration(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [configurationId, setLoading, setAuth]);

  const updateConfigurationToApi = useCallback((values, { setFieldError }) => {
    if (!configurationId) {
      return Promise.resolve();
    }
    const updateBody = {
      fix_value: values.value,
      password: values.is_user ? values.password : null,
      is_user: values.is_user,
    };
    setLoading(true);
    return updateConfiguration(configurationId, updateBody)
      .then(({ data }) => {
        setConfiguration(data);
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [configurationId, setLoading, navigate]);

  useEffect(() => {
    fetchConfigurationFromApi();
  }, [fetchConfigurationFromApi]);

  return (
    <MKBox flex={1} py="2rem">
      <Container>
        <div style={{ marginBottom: 10, color: title_color, fontSize: title_font_size, fontWeight: title_font_weight }}>
          {configuration && `Configurations > ${configuration.name}`}
        </div>
        <EditConfigurationForm
          configuration={configuration}
          onSave={updateConfigurationToApi}
          editButtonColor={edit_button_color}
          editFontColor={edit_font_color}
          saveButtonColor={save_button_color}
          saveButtonFontColor={save_button_font_color}
          backButtonColor={back_button_color}
          backButtonFontColor={back_button_font_color}
        />
      </Container>
    </MKBox>
  );
};

EditConfigurationSection.propTypes = {
  setLoading: PropTypes.func,
  title_color: PropTypes.string,
  title_font_size: PropTypes.string,
  title_font_weight: PropTypes.string,
  edit_button_color: PropTypes.string,
  edit_font_color: PropTypes.string,
  save_button_color: PropTypes.string,
  save_button_font_color: PropTypes.string,
  back_button_color: PropTypes.string,
  back_button_font_color: PropTypes.string,
};

export default withLoading(EditConfigurationSection);
